import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, LogOut, User, Bell, Shield } from 'lucide-react';
import { useUser } from '../context/UserContext';
import { toast } from 'react-toastify';

const Settings = () => {
  const navigate = useNavigate();
  const { user: contextUser, logout } = useUser();
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (!contextUser || !contextUser.telegramName) {
      navigate('/login');
      return;
    }
    setUser(contextUser);
  }, [contextUser, navigate]);

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-slate-700 hover:text-slate-900"
            >
              <ArrowLeft size={20} />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-lg font-semibold text-slate-900">Settings</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
              {user.telegramName?.charAt(1)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">{user.telegramName}</h2>
              <p className="text-sm text-slate-600">{user.email || 'No email provided'}</p>
            </div>
          </div>
        </div>

        {/* Settings Options */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 mb-6">

          <button
            onClick={() => navigate('/account')}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-200"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <User className="text-blue-600" size={20} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Account Details</p>
                <p className="text-sm text-slate-600">View your account information</p>
              </div>
            </div>
            <ArrowLeft className="rotate-180 text-slate-400" size={20} />
          </button>

          <button
            onClick={() => navigate('/change-pin')}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-200"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <Lock className="text-purple-600" size={20} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Change PIN</p>
                <p className="text-sm text-slate-600">Update your security PIN</p>
              </div>
            </div>
            <ArrowLeft className="rotate-180 text-slate-400" size={20} />
          </button>

          <button
            onClick={() => navigate('/account-verification')}
            className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-200"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <Shield className="text-indigo-600" size={20} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Account Verification</p>
                <p className="text-sm text-slate-600">Verify USD, GBP, or YEN accounts</p>
              </div>
            </div>
            <ArrowLeft className="rotate-180 text-slate-400" size={20} />
          </button>

          <button className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors border-b border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
                <Bell className="text-yellow-600" size={20} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Notifications</p>
                <p className="text-sm text-slate-600">Manage notification preferences</p>
              </div>
            </div>
            <ArrowLeft className="rotate-180 text-slate-400" size={20} />
          </button>

          <button className="w-full p-6 flex items-center justify-between hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Shield className="text-green-600" size={20} />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">Security</p>
                <p className="text-sm text-slate-600">Two-factor authentication</p>
              </div>
            </div>
            <ArrowLeft className="rotate-180 text-slate-400" size={20} />
          </button>

        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full p-6 bg-red-50 border border-red-200 rounded-2xl flex items-center justify-center gap-3 hover:bg-red-100 transition-colors"
        >
          <LogOut className="text-red-600" size={20} />
          <span className="font-semibold text-red-600">Logout</span>
        </button>

        {/* App Info */}
        <div className="mt-6 text-center text-sm text-slate-500">
          <p>MODANMIC v1.0.0</p>
          <p className="mt-1">© 2026 MODANMIC. All rights reserved.</p>
        </div>

      </div>
    </div>
  );
};

export default Settings;
